import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/layouts/AdminLayout';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2,
  Play,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Edit2,
  Save,
} from 'lucide-react';

const AdminOrgDetail = () => {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Pipeline
  const [triggeringPipeline, setTriggeringPipeline] = useState(false);
  const [showPipelineConfirm, setShowPipelineConfirm] = useState(false);
  
  // Invite
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);

  const fetchOrg = async () => {
    try {
      const [orgRes, auditRes] = await Promise.all([
        adminApi.getOrg(orgId),
        adminApi.getOrgAuditLog(orgId)
      ]);
      setOrg(orgRes.data);
      setEditData({
        chro_name: orgRes.data.chro_name || '',
        chro_email: orgRes.data.chro_email || '',
        linkedin_url: orgRes.data.linkedin_url || '',
        internal_notes: orgRes.data.internal_notes || '',
      });
      setAuditLog(auditRes.data);
    } catch (error) {
      toast.error('Failed to load organisation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrg();
  }, [orgId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateOrg(orgId, editData);
      toast.success('Organisation updated');
      setEditing(false);
      fetchOrg();
    } catch (error) {
      toast.error('Failed to update organisation');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerPipeline = async () => {
    setTriggeringPipeline(true);
    try {
      await adminApi.triggerPipeline(orgId);
      toast.success('Pipeline triggered successfully');
      setShowPipelineConfirm(false);
      fetchOrg();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger pipeline');
    } finally {
      setTriggeringPipeline(false);
    }
  };

  const handleSendInvite = async () => {
    setSendingInvite(true);
    try {
      await adminApi.sendInvite(orgId);
      toast.success('Invitation sent successfully');
      setShowInviteConfirm(false);
      fetchOrg();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const formatCurrency = (value, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', AED: 'AED ', INR: '₹', MUR: 'Rs ' };
    const symbol = symbols[currency] || '£';
    if (value >= 1000000) return `${symbol}${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${symbol}${(value / 1000).toFixed(0)}K`;
    return `${symbol}${value?.toFixed(0) || 0}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-GB');
  };

  const getStageIcon = (status) => {
    switch (status) {
      case 'complete': return <CheckCircle className="h-4 w-4 text-accent" />;
      case 'failed': return <XCircle className="h-4 w-4 text-destructive" />;
      case 'running': return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      case 'warning': return <AlertCircle className="h-4 w-4 text-warning" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!org) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Organisation not found</p>
          <Button asChild className="mt-4">
            <Link to="/admin/orgs">Back to Organisations</Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const latestRun = org.pipeline_runs?.[org.pipeline_runs.length - 1];
  const enrichment = org.enrichment || {};
  const canTriggerPipeline = org.prepop_status !== 'in_progress';
  const canSendInvite = org.chro_name && org.chro_email && org.prepop_status === 'complete';

  return (
    <AdminLayout
      breadcrumbs={[
        { label: 'Organisations', path: '/admin/orgs' },
        { label: org.name }
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{org.name}</h1>
            <p className="text-muted-foreground">{org.domain}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPipelineConfirm(true)}
              disabled={!canTriggerPipeline}
              data-testid="trigger-pipeline-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              {org.prepop_status === 'in_progress' ? 'Pipeline Running...' : 'Trigger Pipeline'}
            </Button>
            <Button
              onClick={() => setShowInviteConfirm(true)}
              disabled={!canSendInvite}
              data-testid="send-invite-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send CHRO Invite
            </Button>
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex gap-3">
          <Badge variant={org.prepop_status === 'complete' ? 'default' : 'outline'} 
                 className={org.prepop_status === 'complete' ? 'bg-accent/20 text-accent' : ''}>
            Pre-pop: {org.prepop_status?.replace('_', ' ')}
          </Badge>
          <Badge variant={org.chro_status === 'active' ? 'default' : 'outline'}
                 className={org.chro_status === 'active' ? 'bg-accent/20 text-accent' : ''}>
            CHRO: {org.chro_status?.replace('_', ' ')}
          </Badge>
          <Badge variant={org.signal_room_ready ? 'default' : 'outline'}
                 className={org.signal_room_ready ? 'bg-primary/20 text-primary' : ''}>
            Signal Room: {org.signal_room_ready ? 'Ready' : 'Not Ready'}
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="data" className="w-full">
          <TabsList>
            <TabsTrigger value="data">Org Data</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="chro">CHRO & Invite</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          {/* Org Data Tab */}
          <TabsContent value="data" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Seed Data */}
              <Card className="glass-light">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">Seed Data</CardTitle>
                  {!editing ? (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={handleSave} disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium">{org.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Domain</p>
                      <p className="font-medium">{org.domain}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Sector</p>
                      <p className="font-medium">{org.sector?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Geography</p>
                      <p className="font-medium">{org.geography_primary}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Headcount Band</p>
                      <p className="font-medium">{org.headcount_band}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Currency</p>
                      <p className="font-medium">{org.currency}</p>
                    </div>
                  </div>
                  
                  {editing ? (
                    <div className="space-y-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label>LinkedIn URL</Label>
                        <Input
                          value={editData.linkedin_url}
                          onChange={(e) => setEditData({ ...editData, linkedin_url: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CHRO Name</Label>
                        <Input
                          value={editData.chro_name}
                          onChange={(e) => setEditData({ ...editData, chro_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CHRO Email</Label>
                        <Input
                          value={editData.chro_email}
                          onChange={(e) => setEditData({ ...editData, chro_email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Internal Notes</Label>
                        <Input
                          value={editData.internal_notes}
                          onChange={(e) => setEditData({ ...editData, internal_notes: e.target.value })}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3 pt-4 border-t">
                      <div>
                        <p className="text-muted-foreground text-sm">LinkedIn</p>
                        {org.linkedin_url ? (
                          <a href={org.linkedin_url} target="_blank" rel="noopener noreferrer"
                             className="text-sm text-primary hover:underline flex items-center gap-1">
                            {org.linkedin_url}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <p className="text-sm text-muted-foreground">Not set</p>
                        )}
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">CHRO</p>
                        <p className="text-sm font-medium">
                          {org.chro_name || 'Not set'}
                          {org.chro_email && <span className="text-muted-foreground"> ({org.chro_email})</span>}
                        </p>
                      </div>
                      {org.internal_notes && (
                        <div>
                          <p className="text-muted-foreground text-sm">Notes</p>
                          <p className="text-sm">{org.internal_notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Enrichment Data */}
              <Card className="glass-light">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Enrichment Outputs</CardTitle>
                </CardHeader>
                <CardContent>
                  {org.prepop_status === 'not_started' ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Trigger pipeline to enrich organisation data
                    </p>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-background/50">
                          <p className="text-xs text-muted-foreground">Confirmed Headcount</p>
                          <p className="text-2xl font-bold">{enrichment.confirmed_headcount?.toLocaleString() || '-'}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-background/50">
                          <p className="text-xs text-muted-foreground">Active Jobs</p>
                          <p className="text-2xl font-bold">{enrichment.active_job_count || '-'}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-background/50">
                          <p className="text-xs text-muted-foreground">Estimated Labour Cost</p>
                          <p className="text-xl font-bold">{formatCurrency(enrichment.estimated_labour_cost, org.currency)}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-background/50">
                          <p className="text-xs text-muted-foreground">ASBC Gap</p>
                          <p className="text-xl font-bold gradient-text">{formatCurrency(enrichment.asbc_gap, org.currency)}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-lg bg-background/50">
                        <div>
                          <p className="text-xs text-muted-foreground">Confidence Score</p>
                          <p className="text-2xl font-bold">{enrichment.confidence_score || 0}</p>
                        </div>
                        <Badge variant={enrichment.confidence_score >= 60 ? 'default' : 'outline'}
                               className={enrichment.confidence_score >= 60 ? 'bg-accent/20 text-accent' : ''}>
                          {enrichment.confidence_score >= 80 ? 'High' :
                           enrichment.confidence_score >= 60 ? 'Good' :
                           enrichment.confidence_score >= 40 ? 'Moderate' : 'Low'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline" className="mt-6">
            <Card className="glass-light">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">Pipeline Runs</CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchOrg}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {!org.pipeline_runs?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No pipeline runs yet
                  </p>
                ) : (
                  <div className="space-y-4">
                    {org.pipeline_runs.slice().reverse().map((run, index) => (
                      <div key={run.id} className="p-4 rounded-lg bg-background/50">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="font-medium">Run #{org.pipeline_runs.length - index}</p>
                            <p className="text-xs text-muted-foreground">
                              Triggered by {run.triggered_by} at {formatTimestamp(run.triggered_at)}
                            </p>
                          </div>
                          <Badge variant={run.status === 'complete' ? 'default' : 'outline'}
                                 className={run.status === 'complete' ? 'bg-accent/20 text-accent' :
                                           run.status === 'running' ? 'bg-primary/20 text-primary' :
                                           run.status === 'failed' ? 'bg-destructive/20 text-destructive' : ''}>
                            {run.status}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                          {run.stages?.map((stage) => (
                            <div key={stage.stage_number} 
                                 className="flex flex-col items-center p-2 rounded-lg bg-background/30">
                              {getStageIcon(stage.status)}
                              <span className="text-[10px] text-muted-foreground mt-1 text-center">
                                {stage.name.split(' ')[0]}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {run.confidence_score > 0 && (
                          <p className="text-xs text-muted-foreground mt-3">
                            Confidence: {run.confidence_score}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CHRO & Invite Tab */}
          <TabsContent value="chro" className="mt-6">
            <Card className="glass-light">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">CHRO & Invitation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">CHRO Name</p>
                    <p className="font-medium">{org.chro_name || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">CHRO Email</p>
                    <p className="font-medium">{org.chro_email || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Invite Status</p>
                    <p className="font-medium capitalize">{org.chro_status?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Signal Room</p>
                    <p className="font-medium">{org.signal_room_ready ? 'Ready' : 'Not Ready'}</p>
                  </div>
                </div>
                
                {!canSendInvite && (
                  <div className="p-4 rounded-lg bg-warning/10 border border-warning/20">
                    <p className="text-sm text-warning">
                      {!org.chro_name || !org.chro_email 
                        ? 'Set CHRO name and email before sending invite'
                        : 'Complete pre-population pipeline before sending invite'}
                    </p>
                  </div>
                )}

                {org.shareable_links?.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Active Shareable Links</p>
                    <div className="space-y-2">
                      {org.shareable_links.map((link) => (
                        <div key={link.id} className="flex items-center justify-between p-2 rounded bg-background/50 text-sm">
                          <span className="font-mono text-xs">{link.id}</span>
                          <span className="text-muted-foreground">{link.views} views</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="mt-6">
            <Card className="glass-light">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Audit Log</CardTitle>
              </CardHeader>
              <CardContent>
                {auditLog.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No audit events recorded
                  </p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {auditLog.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
                        <div className="flex-1">
                          <p className="text-sm">
                            <span className="font-medium">{log.user_email}</span>
                            <span className="text-muted-foreground"> {log.action?.replace('_', ' ')}</span>
                          </p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {JSON.stringify(log.details)}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Pipeline Confirmation Dialog */}
      <Dialog open={showPipelineConfirm} onOpenChange={setShowPipelineConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trigger Pre-population Pipeline</DialogTitle>
            <DialogDescription>
              This will run the 8-stage enrichment pipeline for {org.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 text-sm">
            <p><strong>Domain:</strong> {org.domain}</p>
            <p><strong>Sector:</strong> {org.sector}</p>
            <p><strong>Geography:</strong> {org.geography_primary}</p>
            <p><strong>Headcount:</strong> {org.headcount_band}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPipelineConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleTriggerPipeline} disabled={triggeringPipeline}>
              {triggeringPipeline ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Triggering...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Trigger Pipeline
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Confirmation Dialog */}
      <Dialog open={showInviteConfirm} onOpenChange={setShowInviteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send CHRO Invitation</DialogTitle>
            <DialogDescription>
              Send a Signal Room invitation to {org.chro_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 text-sm">
            <p><strong>CHRO:</strong> {org.chro_name}</p>
            <p><strong>Email:</strong> {org.chro_email}</p>
            <p><strong>Organisation:</strong> {org.name}</p>
            <p className="text-muted-foreground">The invitation link will expire in 7 days.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendInvite} disabled={sendingInvite}>
              {sendingInvite ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrgDetail;
